/**
 * Global registry of open modals that may have unsaved changes.
 *
 * Each form modal registers itself while mounted (via useUnsavedChanges) and
 * unregisters on unmount. The UnsavedChangesProvider (mounted once in App)
 * reads the registry to decide whether global Escape / navigation /
 * beforeunload events must be intercepted.
 *
 * Registrations keep stack order: the last one is the topmost modal, which
 * gets the close/Escape attention first.
 */
const registrations = new Map();
const stack = [];

/**
 * Registered modal descriptor.
 * @typedef {Object} UnsavedModalRegistration
 * @property {string} id - Stable id for the modal instance.
 * @property {boolean} isDirty - Whether the form currently has unsaved changes.
 * @property {function} guardClose - Modal close entry point (dialog shown when dirty).
 * @property {function} markSaved - Baseline the current snapshot (after save, or
 *                                  when the user chose to discard changes).
 */

export function registerUnsavedModal(id, registration) {
  if (!registrations.has(id)) {
    stack.push(id);
  }
  registrations.set(id, registration);
}

export function unregisterUnsavedModal(id) {
  registrations.delete(id);
  const index = stack.indexOf(id);
  if (index !== -1) {
    stack.splice(index, 1);
  }
}

export function hasUnsavedChanges() {
  return getDirtyModals().length > 0;
}

/** Dirty registrations in stack order (topmost modal last). */
export function getDirtyModals() {
  return stack
    .map((id) => registrations.get(id))
    .filter((registration) => registration && registration.isDirty);
}

/** The topmost dirty registration, if any. */
export function getTopDirtyModal() {
  const dirty = getDirtyModals();
  return dirty.length > 0 ? dirty[dirty.length - 1] : null;
}

/**
 * The provider sets this so Escape / navigation interception while a guard is
 * already pending routes through the dialog's "keep editing" path instead of
 * registering a redundant pending action.
 */
let requestCloseHandler = null;

export function setUnsavedCloseHandler(handler) {
  requestCloseHandler = handler;
}

/**
 * Asks the topmost dirty modal to go through its close guard (global Escape
 * entry point). Returns true if a guard was triggered.
 */
export function requestCloseForDirtyModal() {
  const top = getTopDirtyModal();
  if (!top) {
    return false;
  }
  if (requestCloseHandler) {
    requestCloseHandler(top);
  } else {
    top.guardClose();
  }
  return true;
}
